'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from 'react';
import { distribuirBlocosEmPaginas, type BlocoPaginacaoMeta } from '@/shared/utils/paginacaoDocumento';
import { DocCabecalho, DocPagina, DocRodape } from './DocPagina';
const ALTURA_PAGINA = 1123;
const ALTURA_CABECALHO_COM_RESPIRO = 72;
const ZONA_RODAPE = 70;
const MARGEM_SEGURANCA = 10;
const ALTURA_PRIMEIRA_PAGINA = ALTURA_PAGINA - ZONA_RODAPE - MARGEM_SEGURANCA;
const ALTURA_DEMAIS_PAGINAS = ALTURA_PAGINA - ALTURA_CABECALHO_COM_RESPIRO - ZONA_RODAPE - MARGEM_SEGURANCA;
export interface ContextoPaginaDocumento {
  numeroPagina: number;
  totalPaginas: number;
  /** Permite ao bloco repetir um cabeçalho de continuação quando abre a página. */
  primeiroDaPagina: boolean;
}
export interface BlocoDocumento extends BlocoPaginacaoMeta {
  id: string;
  node?: React.ReactNode;
  render?: (contexto: ContextoPaginaDocumento) => React.ReactNode;
}
function renderizarBloco(bloco: BlocoDocumento, contexto: ContextoPaginaDocumento) {
  return bloco.render ? bloco.render(contexto) : bloco.node;
}

/**
 * Os blocos do início até o primeiro ainda sem medida. Quando o documento cresce (a próxima página
 * de registros chega enquanto a pessoa rola), as folhas já montadas continuam na tela até a medição
 * dos blocos novos: se elas sumissem nesse intervalo, a página encolheria e o navegador levaria a
 * rolagem para o topo.
 */
function blocosJaMedidos(blocos: BlocoDocumento[], alturas: ReadonlyMap<string, number>): BlocoDocumento[] {
  const primeiroSemMedida = blocos.findIndex(bloco => !alturas.has(bloco.id));
  return primeiroSemMedida === -1 ? blocos : blocos.slice(0, primeiroSemMedida);
}
interface DocumentoPaginadoProps {
  blocos: BlocoDocumento[];
  /** Ver `DocRodape`: `null` omite o par no rodapé em vez de imprimir o rótulo sem valor. */
  codigoAutenticacao: string | null;
  urlAutenticacaoExibicao: string | null;
}
export function DocumentoPaginado(t0) {
  const $ = _c(12);
  const {
    blocos,
    codigoAutenticacao,
    urlAutenticacaoExibicao
  } = t0;
  const medidorRef = useRef(null);
  const [alturas, setAlturas] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const medir = () => {
        const nos = medidorRef.current?.querySelectorAll("[data-bloco-medida]");
        if (!nos || nos.length === 0) {
          return;
        }
        setAlturas(new Map(Array.from(nos, _temp2)));
      };
      medir();
      document.fonts?.ready.then(medir);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== blocos) {
    t2 = [blocos];
    $[1] = blocos;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[3] !== alturas || $[4] !== blocos || $[5] !== codigoAutenticacao || $[6] !== urlAutenticacaoExibicao) {
    const medidos = blocosJaMedidos(blocos, alturas);
    const paginas = medidos.length > 0 ? distribuirBlocosEmPaginas({
      blocos: medidos,
      alturas: medidos.map(bloco => alturas.get(bloco.id) ?? 0),
      alturaPrimeiraPagina: ALTURA_PRIMEIRA_PAGINA,
      alturaDemaisPaginas: ALTURA_DEMAIS_PAGINAS
    }) : null;
    let t4;
    if ($[8] !== blocos) {
      t4 = blocos.map(_temp3);
      $[8] = blocos;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t4) {
      t5 = <div ref={medidorRef} data-medidor={true} inert={true} className="pointer-events-none fixed top-0 left-[-2000px] w-[794px] opacity-0">{t4}</div>;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t3 = <>{t5}{paginas?.map((pagina, indexPagina) => <DocPagina key={indexPagina}>{indexPagina > 0 && <DocCabecalho />}{pagina.paginaInteira ? pagina.indices.map((indexBloco, posicao) => <div key={medidos[indexBloco].id} className="flex min-h-0 flex-1 flex-col">{renderizarBloco(medidos[indexBloco], {
            numeroPagina: indexPagina + 1,
            totalPaginas: paginas.length,
            primeiroDaPagina: posicao === 0
          })}</div>) : <div className={`flex-1 ${indexPagina > 0 ? "pt-5" : ""}`}>{pagina.indices.map((indexBloco_0, posicao_0) => <div key={medidos[indexBloco_0].id} className="flow-root w-full">{renderizarBloco(medidos[indexBloco_0], {
              numeroPagina: indexPagina + 1,
              totalPaginas: paginas.length,
              primeiroDaPagina: posicao_0 === 0
            })}</div>)}</div>}{!pagina.paginaInteira && <DocRodape codigoAutenticacao={codigoAutenticacao} urlAutenticacaoExibicao={urlAutenticacaoExibicao} numeroPagina={indexPagina + 1} totalPaginas={paginas.length} />}</DocPagina>)}</>;
    $[3] = alturas;
    $[4] = blocos;
    $[5] = codigoAutenticacao;
    $[6] = urlAutenticacaoExibicao;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp3(bloco_0) {
  return <div key={bloco_0.id} data-bloco-medida={bloco_0.id} className="flow-root w-full">{renderizarBloco(bloco_0, {
      numeroPagina: 0,
      totalPaginas: 0,
      primeiroDaPagina: true
    })}</div>;
}
function _temp2(no) {
  return [no.dataset.blocoMedida ?? "", no.getBoundingClientRect().height];
}
function _temp() {
  return new Map();
}